import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Button from "../../components/Button";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./Home.module.scss";

const Home: FC = () => {
  return (
    <div className={styles.homePage}>
      {/* 1. Hero Section */}
      <Hero
        eyebrow="Pulse Operations &amp; Maintenance"
        heading="Your building information. One place."
        copy="Pulse brings your building documents, drawings, asset information and O&M information together in one secure, structured platform — making the information you need easier to find, understand and use."
        backgroundImage={require("../../assets/images/hero/london-commercial-building.webp")}
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="white" sectionClass={styles.problemSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>The Challenge</span>
          <h2 className={styles.sectionHeading}>
            Building information shouldn't be this difficult to find.
          </h2>
          <p className={styles.sectionCopy}>
            Commercial buildings generate enormous amounts of technical
            information over their lifecycle. Too often, teams rely on
            filenames, folder structures, and Ctrl+F.
          </p>
        </div>
        <div className={styles.problemGrid}>
          <div className={styles.problemCard}>
            <h3 className={styles.problemCardTitle}>The Reality</h3>
            <ul className={styles.problemList}>
              <li>
                Information spread across folders, PDFs, drawings and
                spreadsheets
              </li>
              <li>
                Thousands of documents accumulated over a building's lifecycle
              </li>
              <li>Important information difficult to locate quickly</li>
              <li>
                Knowledge frequently held by individuals rather than systems
              </li>
              <li>O&M information becoming difficult to use after handover</li>
            </ul>
          </div>
          <div className={styles.problemCard}>
            <h3 className={styles.problemCardTitle}>The Impact</h3>
            <ul className={styles.problemList}>
              <li>Time wasted hunting through disconnected files</li>
              <li>Risk of working from outdated document versions</li>
              <li>Difficulty accessing technical details on site</li>
              <li>Inefficient collaboration between teams and contractors</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 3. What Is Pulse? */}
      <Section bgColor="off-white" sectionClass={styles.whatIsPulseSection}>
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Platform Overview</span>
            <h2 className={styles.sectionHeading}>
              A digital information platform for your building.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse creates a structured digital home for your building
              information — connecting documents, drawings, assets and technical
              information in one place.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/digital-building-information-hero.webp")}
              alt="Pulse dashboard showing building overview and structured data"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 4. Digital Building Information */}
      <Section bgColor="white" sectionClass={styles.digitalBuildingSection}>
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/building/building-information.webp")}
              alt="Digital building information record"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Digital Building Record</span>
            <h2 className={styles.sectionHeading}>
              All your building information, organised.
            </h2>
            <p className={styles.sectionCopy}>
              Commercial buildings generate enormous amounts of technical
              information. Pulse brings that information together into a
              structured digital building record, giving your teams a consistent
              place to access what they need.
            </p>
            <ul className={styles.featureList}>
              <li>
                <strong>Documents:</strong> Technical documents, manuals,
                certificates and specifications.
              </li>
              <li>
                <strong>Drawings:</strong> As-built, base-build, post-fitout and
                other drawing information.
              </li>
              <li>
                <strong>Assets:</strong> Equipment and assets connected to the
                information associated with them.
              </li>
              <li>
                <strong>Building information:</strong> A clearer digital record
                of the building and its systems.
              </li>
            </ul>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. Digital O&M */}
      <Section bgColor="off-white" sectionClass={styles.digitalOmSection}>
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Operations & Maintenance</span>
            <h2 className={styles.sectionHeading}>
              Turn the O&M manual into something people actually use.
            </h2>
            <p className={styles.sectionCopy}>
              O&M information shouldn't disappear into a collection of PDFs and
              folders once a project is handed over.
            </p>
            <p className={styles.sectionCopy}>
              Pulse turns existing O&M information into a structured, searchable
              digital resource that building teams can access when they need it.
            </p>
            <ul className={styles.checkList}>
              <li>Find information quickly</li>
              <li>Keep documents and drawings together</li>
              <li>Access information on site</li>
              <li>
                Connect information rather than treating every document
                separately
              </li>
              <li>
                Make existing O&M information more useful throughout the
                building lifecycle
              </li>
            </ul>
            <div className={styles.actionLink}>
              <Link to="/digital-om" className={styles.textLink}>
                Explore Digital O&M →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/mobile/engineer-using-pulse.webp")}
              alt="Engineer using Pulse on site on mobile device"
              className={styles.productScreenshot}
              loading="lazy"
            />
            <div className={styles.imageCaption}>
              Pulse is useful where the building is actually being operated —
              not just at a desk.
            </div>
          </div>
        </div>
      </Section>

      {/* 6. Documents & Drawings */}
      <Section bgColor="white" sectionClass={styles.docsSection}>
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/documents-drawings-hero.webp")}
              alt="Pulse Documents and Drawings interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Documents & Drawings</span>
            <h2 className={styles.sectionHeading}>
              Stop hunting through folders for the right document.
            </h2>
            <p className={styles.sectionCopy}>
              Buildings can contain thousands of technical documents and
              drawings. Pulse gives your team a structured way to organise,
              access and find them.
            </p>
            <div className={styles.tagGrid}>
              <span className={styles.tag}>PDFs</span>
              <span className={styles.tag}>Technical manuals</span>
              <span className={styles.tag}>Certificates</span>
              <span className={styles.tag}>Specifications</span>
              <span className={styles.tag}>CAD/DWG drawings</span>
              <span className={styles.tag}>XREFs</span>
              <span className={styles.tag}>As-built drawings</span>
              <span className={styles.tag}>Base-build drawings</span>
              <span className={styles.tag}>Post-fitout drawings</span>
            </div>
            <div className={styles.actionLink}>
              <Link to="/documents-drawings" className={styles.textLink}>
                Explore Documents & Drawings →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 7. Asset Information */}
      <Section bgColor="off-white" sectionClass={styles.assetSection}>
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Asset Management</span>
            <h2 className={styles.sectionHeading}>
              Connect your assets to the information behind them.
            </h2>
            <p className={styles.sectionCopy}>
              An asset is more than a name and reference number. Pulse can bring
              together the information associated with each asset — from
              manufacturer details and technical documents to drawings,
              maintenance information and warranties.
            </p>

            <div className={styles.assetCardExample}>
              <div className={styles.assetHeader}>
                <span className={styles.assetBadge}>Asset Record</span>
                <h4 className={styles.assetTitle}>AHU-003</h4>
              </div>
              <div className={styles.assetGrid}>
                <span>Manufacturer</span>
                <span>Datasheet</span>
                <span>O&M Info</span>
                <span>Drawings</span>
                <span>Certification</span>
                <span>Maintenance</span>
                <span>Warranty</span>
                <span>Location</span>
              </div>
            </div>

            <p className={styles.qrHighlight}>
              <strong>Scan a QR code on site</strong> and go directly to the
              information for that asset.
            </p>

            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImagesDual}>
            <img
              src={require("../../assets/images/asset-information/asset-information-hero.webp")}
              alt="Asset information interface in Pulse"
              className={styles.productScreenshot}
              loading="lazy"
            />
            <img
              src={require("../../assets/images/assets/asset-qr-code.webp")}
              alt="Asset QR code for on-site scanning"
              className={styles.qrScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 8. Search */}
      <Section bgColor="white" sectionClass={styles.searchSection}>
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-problem.webp")}
              alt="Pulse search interface showing fast results"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Powerful Search</span>
            <h2 className={styles.sectionHeading}>
              Find the information you need.
            </h2>
            <p className={styles.sectionCopy}>
              When a building contains thousands of documents, finding the right
              piece of information can be harder than storing it.
            </p>
            <p className={styles.sectionCopy}>
              Pulse makes building information easier to search, helping users
              get from a question to the relevant document, drawing or asset
              faster.
            </p>
            <div className={styles.searchBoxMock}>
              <div className={styles.searchInputMock}>
                <span>🔍</span> Search: <strong>AHU-003</strong>
              </div>
              <ul className={styles.searchResultsMock}>
                <li>AHU-003 O&M Manual</li>
                <li>AHU-003 Datasheet</li>
                <li>Mechanical Drawing M-402</li>
                <li>Asset Record AHU-003</li>
                <li>Manufacturer Information</li>
              </ul>
            </div>
            <p className={styles.futureNote}>
              <em>
                Pulse can evolve from searching document names and metadata to
                searching document content and asking natural-language questions
                about the building.
              </em>
            </p>
            <div className={styles.actionLink}>
              <Link to="/search" className={styles.textLink}>
                Explore Search →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 9. AI */}
      <Section bgColor="off-white" sectionClass={styles.aiSection}>
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Artificial Intelligence</span>
            <h2 className={styles.sectionHeading}>
              Ask questions about your building.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse AI is designed around your building information. Ask
              questions about your building and get answers based on the
              information stored in Pulse, with links back to the underlying
              documents and records.
            </p>
            <div className={styles.aiQuestionsList}>
              <div className={styles.aiQuestionCard}>
                "What is the recommended filter replacement interval for
                AHU-003?"
              </div>
              <div className={styles.aiQuestionCard}>
                "Which drawings show the chilled water system?"
              </div>
            </div>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information.webp")}
              alt="Pulse AI question answering interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 10. Who It's For */}
      <Section bgColor="white" sectionClass={styles.audienceSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Target Audience</span>
          <h2 className={styles.sectionHeading}>
            Built for the people who need building information.
          </h2>
        </div>
        <div className={styles.audienceGrid}>
          <div className={styles.audienceCard}>
            <h3>Building Owners</h3>
            <p>
              Maintain a long-term digital record of your buildings and their
              information across their lifecycle.
            </p>
          </div>
          <div className={styles.audienceCard}>
            <h3>Property & Facilities Teams</h3>
            <p>
              Find the information you need quickly to operate and manage the
              building with confidence.
            </p>
          </div>
          <div className={styles.audienceCard}>
            <h3>Building Operators</h3>
            <p>
              Access technical information when and where you need it, whether
              at your desk or on site.
            </p>
          </div>
          <div className={styles.audienceCard}>
            <h3>O&M Companies</h3>
            <p>
              Give clients a modern digital platform for the information you
              create, handover and manage.
            </p>
          </div>
        </div>
        <div className={styles.centerAction}>
          <Button to="/who-its-for" variant="secondary" size="md">
            View All Solutions →
          </Button>
        </div>
      </Section>

      {/* 11. Explore the Demo */}
      <CtaSection
        eyebrow="View Demo"
        heading="See what Pulse could do with your building information."
        copy="Whether you're modernising an existing O&M system, improving access to technical information, or creating a digital building platform, Pulse provides a structured foundation."
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(Home)({
  meta: globalContent.seo.home,
});
